// Каталог настроек платформы: id секции → список полей с ключами в БД.
//
// Это единственный источник правды для страницы `sadmin/Settings`:
//   • какие ключи нужно прогрузить при открытии страницы,
//   • как сопоставить ключ с UI-полем (serialize/deserialize),
//   • как называется секция для аудит-лога.
//
// Бэкенд валидирует только структуру (key/value), namespacing и защищает
// секретные префиксы — доменная логика ключей живёт тут, во фронте.

export type SettingValue = string | number | boolean | null | unknown[] | Record<string, unknown>;

export interface FieldDef {
  key: string;
  /** Значение по умолчанию, если ключа нет в БД. */
  default: SettingValue;
  /** true — значение защищено; API возвращает маску. */
  secret?: boolean;
}

export type FieldMap<K extends string> = Record<K, FieldDef>;

// ─── ЮKassa ─────────────────────────────────────────────────────────────
export const YUKASSA_FIELDS = {
  mode:        { key: 'yukassa.mode', default: 'test' },
  shopId:      { key: 'yukassa.shop_id', default: '' },
  secretKey:   { key: 'yukassa.secret_key', default: '', secret: true },
  returnUrl:   { key: 'yukassa.return_url', default: 'https://resstro.ru/cabinet/billing?paid=1' },
  vatCode:     { key: 'yukassa.vat_code', default: '1' },
  autoCapture: { key: 'yukassa.auto_capture', default: true },
  receipt:     { key: 'yukassa.receipt_enabled', default: true },
} satisfies FieldMap<string>;

// ─── SMTP ───────────────────────────────────────────────────────────────
export const SMTP_FIELDS = {
  host:      { key: 'smtp.host', default: 'smtp.yandex.ru' },
  port:      { key: 'smtp.port', default: 465 },
  secure:    { key: 'smtp.secure', default: true },
  user:      { key: 'smtp.user', default: '' },
  password:  { key: 'smtp.password', default: '', secret: true },
  fromName:  { key: 'smtp.from_name', default: 'Resstro' },
  fromEmail: { key: 'smtp.from_email', default: '' },
  replyTo:   { key: 'smtp.reply_to', default: '' },
} satisfies FieldMap<string>;

// ─── SMS ────────────────────────────────────────────────────────────────
export const SMS_FIELDS = {
  enabled:  { key: 'sms.enabled', default: false },
  provider: { key: 'sms.provider', default: 'smsru' },
  apiKey:   { key: 'sms.api_key', default: '', secret: true },
  sender:   { key: 'sms.sender', default: 'Resstro' },
} satisfies FieldMap<string>;

// ─── Регистрация ────────────────────────────────────────────────────────
export const SIGNUP_FIELDS = {
  trialDays:       { key: 'signup.trial_days', default: 3 },
  codeTtlMin:      { key: 'signup.code_ttl_minutes', default: 10 },
  cooldownSec:     { key: 'signup.cooldown_seconds', default: 60 },
  maxAttempts:     { key: 'signup.max_attempts', default: 5 },
  requirePhone:    { key: 'signup.require_phone', default: true },
  requireConsent:  { key: 'signup.require_consent', default: true },
  autoCreateDemo:  { key: 'signup.auto_create_demo', default: false },
} satisfies FieldMap<string>;

// ─── Feature flags (глобальные) ─────────────────────────────────────────
// Все флаги живут в namespace `feature.*`, значения — boolean.
export interface GlobalFlagDef {
  key: string;       // без префикса, UI отображает как name.space
  name: string;
  description: string;
  rollout?: 'all' | 'beta' | 'off';
}

export const GLOBAL_FLAGS: GlobalFlagDef[] = [
  {
    key: 'cashier.apple_pay',
    name: 'Apple Pay в кассе',
    description: 'Приём Apple Pay через Tap to Pay on iPhone. Требует Merchant ID.',
    rollout: 'beta',
  },
  {
    key: 'menu.stories',
    name: 'Stories в электронном меню',
    description: 'Сюжеты-истории блюд на первом экране меню (как в Instagram).',
    rollout: 'all',
  },
  {
    key: 'admin.ai_assistant',
    name: 'AI-ассистент в админке',
    description: 'Подсказки и автопилот по меню/ценам на базе LLM.',
    rollout: 'off',
  },
  {
    key: 'loyalty.cashback',
    name: 'Кэшбэк-программа',
    description: 'Начисление бонусов 1% при каждой оплате гостем в меню.',
  },
  {
    key: 'public_api.v2',
    name: 'Публичный API v2',
    description: 'Новая версия REST API с пагинацией и filter-expressions.',
    rollout: 'beta',
  },
];

export const globalFlagKey = (subKey: string) => `feature.${subKey}`;

// ─── Системный баннер ───────────────────────────────────────────────────
export const BANNER_FIELDS = {
  enabled:     { key: 'banner.enabled', default: false },
  tone:        { key: 'banner.tone', default: 'info' },
  message:     { key: 'banner.message', default: '' },
  dismissible: { key: 'banner.dismissible', default: true },
} satisfies FieldMap<string>;

// ─── Интеграции ─────────────────────────────────────────────────────────
export const INTEGRATIONS_FIELDS = {
  apnsKeyId:       { key: 'integrations.apns.key_id', default: '' },
  apnsTeamId:      { key: 'integrations.apns.team_id', default: '' },
  apnsBundleId:    { key: 'integrations.apns.bundle_id', default: 'ru.resstro.cashier' },
  telegramToken:   { key: 'integrations.telegram.bot_token', default: '', secret: true },
  telegramChatId:  { key: 'integrations.telegram.alerts_chat_id', default: '' },
  s3Endpoint:      { key: 'integrations.s3.endpoint', default: 'storage.yandexcloud.net' },
  s3Bucket:        { key: 'integrations.s3.bucket', default: 'resstro-uploads' },
  s3Region:        { key: 'integrations.s3.region', default: 'ru-central1' },
  s3AccessKey:     { key: 'integrations.s3.access_key', default: '' },
  s3SecretKey:     { key: 'integrations.s3.secret_key', default: '', secret: true },
  sentryDsn:       { key: 'integrations.sentry.dsn', default: '', secret: true },
} satisfies FieldMap<string>;

// ─── Бренд и домены ─────────────────────────────────────────────────────
export const BRANDING_FIELDS = {
  brandName:   { key: 'branding.name', default: 'Resstro' },
  brandColor:  { key: 'branding.color', default: '#E85624' },
  landingUrl:  { key: 'branding.landing_url', default: 'https://resstro.ru' },
  adminUrl:    { key: 'branding.admin_url', default: 'https://app.resstro.ru' },
  menuRoot:    { key: 'branding.menu_root_domain', default: 'menu.resstro.ru' },
  logoLight:   { key: 'branding.logo_light_url', default: '' },
  logoDark:    { key: 'branding.logo_dark_url', default: '' },
} satisfies FieldMap<string>;

// ─── Юр. документы ──────────────────────────────────────────────────────
export const LEGAL_FIELDS = {
  offerUrl:    { key: 'legal.offer_url', default: '/legal/offer' },
  privacyUrl:  { key: 'legal.privacy_url', default: '/legal/privacy' },
  consentUrl:  { key: 'legal.consent_url', default: '/legal/consent' },
  companyName: { key: 'legal.company_name', default: '' },
  companyInn:  { key: 'legal.company_inn', default: '' },
} satisfies FieldMap<string>;

// ─── Поддержка ──────────────────────────────────────────────────────────
export const SUPPORT_FIELDS = {
  email:       { key: 'support.email', default: 'resstrosaas@mail.ru' },
  whatsapp:    { key: 'support.whatsapp', default: '+7 989 473-43-22' },
  phone:       { key: 'support.phone', default: '+7 989 473-43-22' },
  sla:         { key: 'support.sla', default: '4h' },
  weekendMode: { key: 'support.weekend_mode', default: true },
} satisfies FieldMap<string>;

// ─── Безопасность ───────────────────────────────────────────────────────
export const SECURITY_FIELDS = {
  sessionTtlDays:     { key: 'security.session_ttl_days', default: 30 },
  adminSessionTtlMin: { key: 'security.admin_session_ttl_minutes', default: 60 },
  publicRps:          { key: 'security.public_rps', default: 10 },
  operatorRps:        { key: 'security.operator_rps', default: 5 },
  ipAllowlist:        { key: 'security.ip_allowlist', default: '' },
  mfaRequired:        { key: 'security.mfa_required_for_operators', default: true },
  auditRetentionDays: { key: 'security.audit_retention_days', default: 365 },
} satisfies FieldMap<string>;

// ─── Отдел продаж (manager.resstro.ru) ──────────────────────────────────
// Бэкенд читает те же ключи в modules/sales/sales-settings.ts.
export const SALES_FIELDS = {
  safePercent: { key: 'sales.safe_percent', default: 40 },
  safeStart:   { key: 'sales.safe_start', default: '2026-10-01' },
} satisfies FieldMap<string>;

// ─── Все ключи, которые страница настроек должна уметь читать ───────────
// Используется в хуке useSettings для фильтрации ответа listSettings()
// и проверки, что мы не «забыли» ключ при расширении.
export const ALL_TRACKED_PREFIXES = [
  'yukassa.',
  'smtp.',
  'sms.',
  'signup.',
  'feature.',
  'banner.',
  'integrations.',
  'branding.',
  'legal.',
  'support.',
  'security.',
  'sales.',
];

export function isTrackedKey(key: string): boolean {
  return ALL_TRACKED_PREFIXES.some((p) => key.startsWith(p));
}

/** Определяет, похоже ли значение на маску секрета (скопировано с бэкенда). */
export function looksMasked(value: unknown): boolean {
  return typeof value === 'string' && (value === '•••' || value.startsWith('•••• '));
}
